import { AbstractDimension, deepEqualAssimilateNullUndefined } from '@orus.eu/dimensions'
import type { FrPrelQuote } from '@orus.eu/pricing'
import type { Result } from '@orus.eu/result'

import { omitOrusHttpRequestId, omitResultOrusHttpRequestId } from './kanonik-request-id.ts'

export type FrPrelQuoteResult = Result<
  FrPrelQuote,
  | { type: 'map-fr-prel-guarantees-to-kanonik-guarantee-selection-failed'; message: string }
  | { type: 'set_product_selection_failed'; message: string }
  | { type: 'get_price_failed'; message: string }
  | { type: 'exclusions_present'; message: string }
>

export class FrPrelQuoteDimension<NAME extends string> extends AbstractDimension<NAME, FrPrelQuoteResult> {
  override formatToString(value: FrPrelQuoteResult): string {
    if (value.type === 'failure') {
      return `NULL - ${value.problem.type}`
    }
    return JSON.stringify(value.output)
  }

  /**
   * The quote input keeps the id of the Kanonik price request and the price's `createdAt`, and both are new on every
   * call, even when Kanonik computes the same price for the same input.
   */
  override areValuesEquivalent(
    a: FrPrelQuoteResult | null | undefined,
    b: FrPrelQuoteResult | null | undefined,
  ): boolean {
    return deepEqualAssimilateNullUndefined(omitVolatilePriceFields(a), omitVolatilePriceFields(b))
  }
}

function omitVolatilePriceFields(value: FrPrelQuoteResult | null | undefined): unknown {
  if (value == undefined) return value
  switch (value.type) {
    case 'success': {
      const { kanonikPrice, ...input } = value.output.input
      const { createdAt: _ignoredCreatedAt, ...significantKanonikPrice } = kanonikPrice
      return {
        type: 'success',
        output: { ...value.output, input: { ...omitOrusHttpRequestId(input), kanonikPrice: significantKanonikPrice } },
      }
    }
    case 'failure':
      return omitResultOrusHttpRequestId(value)
  }
}

export const frPrelQuoteDimension = new FrPrelQuoteDimension({
  name: 'frPrelQuote',
  displayKeys: { name: 'dimension_name_fr_prel_quote' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
